/**
 * A local service (§5.9.1) as the workspace page types it, and back: the ports,
 * the environment and the volumes are one line each in three text fields,
 * because that is how a person already writes them in a compose file.
 *
 *   ports        5432:5432, 8080:80        (machine:container, or one number for both)
 *   environment  POSTGRES_USER=app          (a value, kept in the document)
 *                POSTGRES_PASSWORD=${api/DB_PASSWORD}
 *                                          (read on each machine from the variable
 *                                           DB_PASSWORD of the project called api)
 *   volumes      data:/var/lib/postgresql/data
 *
 * What comes out is checked by the same `localServiceProblem` the app and the
 * Worker use, so a refusal is said under the form rather than at publish.
 */
import {
  localServiceProblem,
  type LocalService,
  type LocalServiceEnv,
  type LocalServicePort,
  type LocalServiceVolume
} from '@airlock/contracts/settings/localservice';
import type { DocProject } from '@airlock/contracts/workspace/document';

export interface LocalServiceDraft {
  name: string;
  image: string;
  ports: string;
  env: string;
  volumes: string;
}

export const BLANK_SERVICE: LocalServiceDraft = { name: '', image: '', ports: '', env: '', volumes: '' };

const REFERENCE = /^\$\{([^/}]+)\/([^}]+)\}$/;

function parsePorts(text: string): LocalServicePort[] | string {
  const out: LocalServicePort[] = [];
  for (const part of text.split(/[\s,]+/).filter(Boolean)) {
    const match = /^(\d{1,5})(?::(\d{1,5}))?$/.exec(part);
    if (!match) return `${part} is not a port — write 5432, or 15432:5432 for the machine’s then the container’s.`;
    out.push({ host: Number(match[1]), container: Number(match[2] ?? match[1]) });
  }
  return out;
}

function parseEnv(text: string, repos: readonly Pick<DocProject, 'id' | 'name' | 'env'>[]): LocalServiceEnv[] | string {
  const out: LocalServiceEnv[] = [];
  for (const line of text.split('\n').map((one) => one.trim()).filter(Boolean)) {
    const at = line.indexOf('=');
    if (at <= 0) return `${line}: write KEY=value, or KEY=\${project/VARIABLE} to read a project’s variable.`;
    const key = line.slice(0, at).trim();
    const value = line.slice(at + 1).trim();
    const reference = REFERENCE.exec(value);
    if (!reference) {
      out.push({ key, value, from: null });
      continue;
    }
    const repo = repos.find((one) => one.name === reference[1].trim());
    if (!repo) return `${key}: no project is called ${reference[1].trim()}.`;
    const variable = reference[2].trim();
    if (!repo.env.some((one) => one.key === variable)) return `${key}: ${repo.name} has no variable ${variable}.`;
    out.push({ key, value: null, from: { repoId: repo.id, key: variable } });
  }
  return out;
}

function parseVolumes(text: string): LocalServiceVolume[] | string {
  const out: LocalServiceVolume[] = [];
  for (const line of text.split(/[\n,]+/).map((one) => one.trim()).filter(Boolean)) {
    const at = line.indexOf(':');
    if (at <= 0) return `${line}: write name:/path/in/container — a named volume, never a folder of the machine.`;
    out.push({ name: line.slice(0, at).trim(), path: line.slice(at + 1).trim() });
  }
  return out;
}

/** The draft as a declaration, or the one sentence that says why it is not one yet. */
export function serviceFromDraft(
  draft: LocalServiceDraft,
  repos: readonly Pick<DocProject, 'id' | 'name' | 'env'>[],
  id: string
): LocalService | string {
  const ports = parsePorts(draft.ports);
  if (typeof ports === 'string') return ports;
  const env = parseEnv(draft.env, repos);
  if (typeof env === 'string') return env;
  const volumes = parseVolumes(draft.volumes);
  if (typeof volumes === 'string') return volumes;
  const service: LocalService = { id, name: draft.name.trim(), image: draft.image.trim(), ports, env, volumes };
  return localServiceProblem(service) ?? service;
}

/** A declaration written back the way the form reads it — what Edit opens on. */
export function draftFromService(
  service: LocalService,
  repos: readonly Pick<DocProject, 'id' | 'name'>[]
): LocalServiceDraft {
  return {
    name: service.name,
    image: service.image,
    ports: service.ports.map((port) => (port.host === port.container ? `${port.host}` : `${port.host}:${port.container}`)).join(', '),
    env: service.env
      .map((entry) => {
        if (!entry.from) return `${entry.key}=${entry.value ?? ''}`;
        const repo = repos.find((one) => one.id === entry.from?.repoId);
        return `${entry.key}=\${${repo?.name ?? entry.from.repoId}/${entry.from.key}}`;
      })
      .join('\n'),
    volumes: service.volumes.map((volume) => `${volume.name}:${volume.path}`).join('\n')
  };
}
